import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';

import { environment } from '../environments/environment';
import { AuthService } from '@core/services/auth.service';
import { BannerService } from '@core/services/banner.service';
import { BannerSocketService } from '@core/services/socket.service';
import { IBanner } from '@core/interfaces/ibanner';
import { ILoginResponse } from '@core/interfaces/ilogin-response';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  title = 'prism-client';

  private router = inject(Router);
  private authService = inject(AuthService);
  private bannerService = inject(BannerService);
  private bannerSocket = inject(BannerSocketService);

  private subs = new Subscription();

  private user$ = toObservable<ILoginResponse | null>(this.authService.currentUserInfo);

  isLoggedIn$ = this.user$.pipe(
    map(u => !!u),
    startWith(this.authService.checkLogin())
  );

  banner$ = this.bannerSocket.banner$;

  @ViewChild('marqueeContent') marqueeContent?: ElementRef<HTMLElement>;

  // SCSS variables for animation
  marqueeDuration = '60s'; // computed each time; default is harmless
  marqueeDelay = '0s';     // negative delay so text is visible immediately
  startX = 0;              // px; start off the right edge
  endX = 0;                // px; end off the left edge

  private readonly speedPerSec = 60; // lower = slower, higher = faster
  private readonly minSeconds = 20;          // never faster than this
  private readonly maxSeconds = 180;         // never slower than this
  private readonly initialPeek = 48;  // how far inside from right at t=0

  ngOnInit(): void {
    if (environment.isSiteOffline) {
      this.authService.logout(true, false, true);
      // (isUnauthorized = true, isIdleTimeout = false, silent = true)
    }
    // load Google Analytics if tracking ID is configured
    if (environment.gaTrackingId) {
      this.loadGoogleAnalytics(environment.gaTrackingId);
    }

    // If already logged in at app start: start socket + seed + session monitor
    if (this.authService.checkLogin()) {
      this.bannerSocket.start();
      this.subs.add(
        this.bannerService.getBanner().subscribe(b => this.bannerSocket.seed(b))
      );
      this.authService.startSessionMonitor();
    }

    this.subs.add(
      this.user$.subscribe(user => {
        if (user) {
          this.bannerSocket.start();
          this.subs.add(
            this.bannerService.getBanner().subscribe(b => this.bannerSocket.seed(b))
          );
        } else {
          this.bannerSocket.stop();
          this.bannerSocket.seed({ message: '', isActive: false } as IBanner);
        }
        requestAnimationFrame(() => this.updateMarqueeMetrics());
      })
    );

    this.subs.add(
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd))
        .subscribe(() => {
          const mainContent = document.getElementById('main-content');
          if (mainContent) setTimeout(() => mainContent.focus(), 50);
        })
    );
  }

  ngAfterViewInit(): void {
    // Recompute marquee metrics whenever the live banner changes
    this.subs.add(
      this.banner$.subscribe(b => {
        if (b?.isActive && b.message?.trim()) {
          requestAnimationFrame(() => this.updateMarqueeMetrics());
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.bannerSocket.stop();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateMarqueeMetrics();
  }

  private updateMarqueeMetrics(): void {
    const contentEl = this.marqueeContent?.nativeElement;
    const containerEl = contentEl?.parentElement as HTMLElement | undefined;
    if (!contentEl || !containerEl) return;

    const contentWidth = Math.ceil(contentEl.scrollWidth);
    const containerWidth = Math.ceil(containerEl.clientWidth);
    if (!contentWidth || !containerWidth) return;

    // Start just off the right; end off the left
    this.startX = containerWidth;
    this.endX = -contentWidth;

    const distance = this.startX - this.endX;

    // Constant perceived speed
    const speed = this.speedPerSec;
    const seconds = Math.min(this.maxSeconds, Math.max(this.minSeconds, distance / speed));
    this.marqueeDuration = `${seconds}s`;

    // Negative delay so it's visible immediately at initialPeek inside the right edge
    const enterOffsetPx = Math.min(this.initialPeek, containerWidth);
    const tEnter = enterOffsetPx / speed;
    this.marqueeDelay = `-${tEnter.toFixed(3)}s`;
  }

  private loadGoogleAnalytics(trackingId: string): void {
    // load gtag script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    document.head.appendChild(script1);

    // initialize gtag
    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${trackingId}');
    `;
    document.head.appendChild(script2);
  }

  skipToContent(): void {
    const currentUrl = this.router.url.split('#')[0];
    this.router.navigateByUrl(currentUrl + '#main-content').then(() => {
      const mainContent = document.getElementById('main-content');
      if (mainContent) {
        mainContent.focus();
        mainContent.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }
}
